import { useCallback, useRef } from 'react';

import type { TranslationKey } from '@/i18n/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnState, GridApi } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { gridLayoutQueries, saveGridLayout, type GridLayoutData } from '../api/queries';
import type { ColumnStateData, GridLayoutState } from '../api/schema';

interface UseGridLayoutOptions {
  tableId: string;
  enabled?: boolean;
}

interface UseGridLayoutReturn {
  saveLayout: () => void;
  isSaving: boolean;
  isLayoutReady: boolean;
  hasSavedLayout: boolean;
  savedVisibleColumnIds: string[] | null;
  savedColumnOrder: string[] | null;
  savedColumnWidths: Record<string, number> | null;
  applyLayoutToGrid: (api: GridApi) => void;
}

const extractVisibleColumnIds = (columnState: ColumnStateData[]): string[] => {
  return columnState.filter((col) => col.hide !== true).map((col) => col.colId);
};

const extractColumnWidths = (columnState: ColumnStateData[]): Record<string, number> => {
  const widths: Record<string, number> = {};
  for (const col of columnState) {
    if (col.width !== undefined) {
      widths[col.colId] = col.width;
    }
  }
  return widths;
};

export const useGridLayout = (
  gridApi: GridApi | null,
  { tableId, enabled = true }: UseGridLayoutOptions
): UseGridLayoutReturn => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const appliedForTableIdRef = useRef<string | null>(null);

  // Store the initial layout state - this is "frozen" after first load to prevent
  // UI flicker when saving. AG Grid becomes the source of truth after initial render.
  const initialLayoutRef = useRef<{
    tableId: string;
    columnOrder: string[];
    visibleColumnIds: string[];
    columnWidths: Record<string, number>;
  } | null>(null);

  // Load saved layout data
  const { data: savedLayoutData, isFetched } = useQuery({
    ...gridLayoutQueries.detail(tableId),
    enabled: enabled && !!tableId
  });

  const columnState = savedLayoutData?.layoutState?.columnState;
  const isLayoutReady = !enabled || isFetched;
  const hasSavedLayout = Boolean(columnState && columnState.length > 0);

  // Freeze the initial layout on first successful load for this tableId
  // This prevents UI flicker when saving - after initial load, AG Grid is the source of truth
  if (hasSavedLayout && columnState && initialLayoutRef.current?.tableId !== tableId) {
    initialLayoutRef.current = {
      tableId,
      columnOrder: columnState.map((col) => col.colId),
      visibleColumnIds: extractVisibleColumnIds(columnState),
      columnWidths: extractColumnWidths(columnState)
    };
  }

  // Reset frozen state when tableId changes to a table without saved layout
  if (!hasSavedLayout && initialLayoutRef.current?.tableId === tableId) {
    initialLayoutRef.current = null;
  }

  // Use frozen initial state for rendering, not the live query data
  const currentLayout = initialLayoutRef.current?.tableId === tableId ? initialLayoutRef.current : null;
  const savedVisibleColumnIds = currentLayout?.visibleColumnIds ?? null;
  const savedColumnOrder = currentLayout?.columnOrder ?? null;
  const savedColumnWidths = currentLayout?.columnWidths ?? null;

  const applyLayoutToGrid = useCallback(
    (api: GridApi) => {
      const hasColumnState = columnState !== undefined && columnState.length > 0;
      if (!hasColumnState || !enabled || appliedForTableIdRef.current === tableId) {
        return;
      }

      // Get current pinned state from column definitions
      const currentState = api.getColumnState();
      const pinnedMap = new Map(
        currentState
          .filter((col) => col.pinned !== null && col.pinned !== undefined)
          .map((col) => [col.colId, col.pinned])
      );

      // Merge saved state with preserved pinned values
      const mergedState = columnState.map((col) => {
        const pinnedValue = pinnedMap.get(col.colId);
        if (pinnedValue !== null && pinnedValue !== undefined) {
          return { ...col, pinned: pinnedValue };
        }
        return col;
      });

      // Add flex: 1 to the first non-pinned column so it fills remaining viewport space
      const firstNonPinnedIdx = mergedState.findIndex((col) => col.pinned !== 'right');
      const firstNonPinnedCol = mergedState[firstNonPinnedIdx];
      if (firstNonPinnedCol) {
        mergedState[firstNonPinnedIdx] = { ...firstNonPinnedCol, colId: firstNonPinnedCol.colId, flex: 1 };
      }

      api.applyColumnState({
        state: mergedState as ColumnState[],
        applyOrder: true
      });
      appliedForTableIdRef.current = tableId;
    },
    [columnState, enabled, tableId]
  );

  // Save layout mutation - backend handles upsert automatically
  const { mutate: saveLayoutMutation, isPending: isSaving } = useMutation({
    mutationFn: (layoutState: GridLayoutState) => saveGridLayout(tableId, layoutState),
    onSuccess: (_settingId, variables) => {
      toast.success(t('grid.layout.saveSuccess' as TranslationKey));
      // Optimistic update - immediately update cache with new data
      queryClient.setQueryData<GridLayoutData>(gridLayoutQueries.byCode(tableId), (old) => ({
        settingId: old?.settingId ?? _settingId,
        layoutState: variables
      }));
    },
    onError: (error) => {
      console.error('Failed to save grid layout:', error);
      toast.error(t('grid.layout.saveError' as TranslationKey));
    }
  });

  const saveLayout = useCallback(() => {
    if (!gridApi) {
      console.warn('Grid API not available');
      return;
    }

    const currentColumnState = gridApi.getColumnState();
    const layoutState: GridLayoutState = {
      columnState: currentColumnState as ColumnStateData[]
    };

    saveLayoutMutation(layoutState);
  }, [gridApi, saveLayoutMutation]);

  return {
    saveLayout,
    isSaving,
    isLayoutReady,
    hasSavedLayout,
    savedVisibleColumnIds,
    savedColumnOrder,
    savedColumnWidths,
    applyLayoutToGrid
  };
};
